import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
} from 'firebase/firestore';
import {
  COLLATERAL_ID,
  landingPath,
  type CampaignCollateral,
  type Thread,
  type ThreadMessage,
  type TimestampLike,
} from '@revbase/shared';
import {
  Inbox as InboxIcon,
  MessageSquare,
  Send,
  Check,
  Ban,
  AlertCircle,
  FileText,
  Link2,
} from 'lucide-react';
import { db } from '../lib/firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { api } from '../lib/api.js';
import {
  Card,
  Button,
  Badge,
  Textarea,
  PageHeader,
} from '../components/ui.js';
import { clsx } from 'clsx';

interface ThreadRow extends Thread {
  id: string;
}
interface MessageRow extends ThreadMessage {
  id: string;
}

/** Compact relative time from a Firestore-style timestamp. */
function relTime(ts: TimestampLike | undefined): string {
  if (!ts) return '';
  const then = ts.seconds * 1000;
  const min = Math.floor((Date.now() - then) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(then).toLocaleDateString();
}

/** Full local timestamp for a message header. */
function fullTime(ts: TimestampLike | undefined): string {
  if (!ts) return '';
  return new Date(ts.seconds * 1000).toLocaleString();
}

export function Inbox() {
  const { tenantId } = useAuth();
  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The selected thread's campaign collateral, for one-click reply snippets
  // (COLLATERAL_SPEC §10, collateral type d). No extra AI at insert or send time.
  const [collateral, setCollateral] = useState<CampaignCollateral | null>(null);

  // Live thread list, newest first. The unread filter uses the composite index.
  useEffect(() => {
    if (!tenantId) return;
    const col = collection(db, `tenants/${tenantId}/threads`);
    const q = unreadOnly
      ? query(
          col,
          where('unread', '==', true),
          orderBy('lastMessageAt', 'desc'),
          limit(200),
        )
      : query(col, orderBy('lastMessageAt', 'desc'), limit(200));
    return onSnapshot(q, (snap) => {
      setThreads(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Thread) })),
      );
    });
  }, [tenantId, unreadOnly]);

  // Live conversation for the selected thread, oldest first.
  useEffect(() => {
    if (!tenantId || !selectedId) {
      setMessages([]);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/threads/${selectedId}/messages`),
      orderBy('sentAt', 'asc'),
    );
    return onSnapshot(q, (snap) => {
      setMessages(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as ThreadMessage) })),
      );
    });
  }, [tenantId, selectedId]);

  const selected = useMemo(
    () => threads.find((t) => t.id === selectedId) ?? null,
    [threads, selectedId],
  );

  const unreadCount = useMemo(
    () => threads.filter((t) => t.unread).length,
    [threads],
  );

  // Load the selected thread's campaign collateral so the composer can offer the
  // one-pager / case-study reply snippets. A single point read per thread open.
  useEffect(() => {
    const campaignId = selected?.campaignId;
    if (!tenantId || !campaignId) {
      setCollateral(null);
      return;
    }
    void getDoc(
      doc(db, `tenants/${tenantId}/campaigns/${campaignId}/collateral/${COLLATERAL_ID}`),
    )
      .then((snap) =>
        setCollateral(snap.exists() ? (snap.data() as CampaignCollateral) : null),
      )
      .catch(() => setCollateral(null));
  }, [tenantId, selected?.campaignId]);

  const insertSnippet = (text: string) => {
    setReply((r) => (r.trim() ? `${r.trimEnd()}\n\n${text}` : text));
  };

  const insertOnePager = () => {
    const onePager = collateral?.replyAssets?.onePager;
    if (onePager) insertSnippet(onePager);
  };

  const insertCaseStudy = () => {
    const ra = collateral?.replyAssets;
    if (!ra) return;
    const url = collateral?.landingSlug
      ? `${window.location.origin}${landingPath(collateral.landingSlug)}`
      : collateral?.sourceUrl;
    const label = ra.caseStudyLabel || 'Case study';
    insertSnippet(url ? `${label}: ${url}` : label);
  };

  const openThread = (t: ThreadRow) => {
    setSelectedId(t.id);
    setError(null);
    // Clear-on-open: mark read when the operator opens an unread thread.
    if (t.unread) {
      void api.setThreadRead({ threadId: t.id, read: true }).catch(() => {});
    }
  };

  const send = async () => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    setError(null);
    try {
      await api.sendReply({ threadId: selected.id, body: reply });
      setReply('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const toggleRead = async () => {
    if (!selected) return;
    try {
      await api.setThreadRead({
        threadId: selected.id,
        read: selected.unread,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    }
  };

  const notInterested = async () => {
    if (!selected) return;
    setError(null);
    try {
      await api.markThreadNotInterested({ threadId: selected.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={InboxIcon}
        eyebrow="Replies"
        title="Inbox"
        actions={
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-600 shadow-sm transition duration-200 hover:border-slate-300">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-500"
            />
            Unread only
            {unreadCount > 0 && <Badge variant="brand">{unreadCount}</Badge>}
          </label>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
        {/* Thread list */}
        <Card className="max-h-[70vh] overflow-y-auto p-0">
          {threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/10">
                <MessageSquare className="h-6 w-6" strokeWidth={1.75} aria-hidden />
              </span>
              <p className="mt-3 text-sm text-slate-500">
                {unreadOnly
                  ? 'No unread replies.'
                  : 'No replies yet. When a prospect replies to a campaign, the conversation lands here.'}
              </p>
            </div>
          ) : (
            <ul>
              {threads.map((t) => {
                const active = t.id === selectedId;
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => openThread(t)}
                      className={clsx(
                        'w-full border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-b-0',
                        active ? 'bg-brand-50' : 'hover:bg-slate-50',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={clsx(
                            'truncate font-mono text-[13px]',
                            t.unread
                              ? 'font-semibold text-slate-900'
                              : 'text-slate-700',
                          )}
                        >
                          {t.contactName?.trim() || t.contactEmail}
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          {t.unread && (
                            <span
                              aria-label="unread"
                              className="h-2 w-2 rounded-full bg-brand-600"
                            />
                          )}
                          <span className="font-mono text-[11px] text-slate-400">
                            {relTime(t.lastMessageAt)}
                          </span>
                        </span>
                      </div>
                      <p
                        className={clsx(
                          'mt-1 truncate text-sm',
                          t.unread ? 'text-slate-700' : 'text-slate-500',
                        )}
                      >
                        {t.latestDirection === 'outbound' && (
                          <span className="text-slate-400">You: </span>
                        )}
                        {t.latestSnippet}
                      </p>
                      <p className="mt-1 truncate font-mono text-[11px] text-slate-400">
                        {t.campaignName} via {t.mailboxEmail}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Conversation */}
        <Card className="flex max-h-[70vh] flex-col p-0">
          {!selected ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/10">
                <InboxIcon className="h-7 w-7" strokeWidth={1.75} aria-hidden />
              </span>
              <p className="text-sm text-slate-500">
                Select a conversation to read and reply.
              </p>
            </div>
          ) : (
            <>
              <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 p-4">
                <div className="min-w-0">
                  <h2 className="truncate font-display text-base font-semibold text-slate-900">
                    {selected.subject || '(no subject)'}
                  </h2>
                  <p className="mt-0.5 truncate font-mono text-[12px] text-slate-500">
                    {selected.contactName?.trim()
                      ? `${selected.contactName} `
                      : ''}
                    {selected.contactEmail}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {selected.notInterested && (
                    <Badge variant="bad" dot>
                      not interested
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    icon={Check}
                    className="px-3 py-1.5 text-xs"
                    onClick={() => void toggleRead()}
                  >
                    {selected.unread ? 'Mark read' : 'Mark unread'}
                  </Button>
                  <Button
                    variant="destructive"
                    icon={Ban}
                    className="px-3 py-1.5 text-xs"
                    onClick={() => void notInterested()}
                    disabled={selected.notInterested}
                  >
                    Not interested
                  </Button>
                </div>
              </header>

              <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/50 p-4">
                {messages.map((m) => {
                  const inbound = m.direction === 'inbound';
                  return (
                    <div
                      key={m.id}
                      className={clsx('flex', inbound ? 'justify-start' : 'justify-end')}
                    >
                      <div
                        className={clsx(
                          'max-w-[85%] rounded-xl border px-4 py-3',
                          inbound
                            ? 'border-slate-200 bg-slate-50'
                            : 'border-brand-100 bg-brand-50',
                        )}
                      >
                        <div className="mb-1 flex items-center gap-2 font-mono text-[11px] text-slate-400">
                          <span className="truncate">
                            {inbound ? m.from : `${m.from} (you)`}
                          </span>
                          <span>{fullTime(m.sentAt)}</span>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
                          {m.bodyText || m.snippet}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-200 p-4">
                {selected.notInterested ? (
                  <p className="text-sm text-slate-500">
                    This contact is suppressed. Replying is disabled.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {collateral?.replyAssets && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="ghost"
                          icon={FileText}
                          className="px-3 py-1.5 text-xs"
                          onClick={insertOnePager}
                        >
                          Insert one-pager
                        </Button>
                        <Button
                          variant="ghost"
                          icon={Link2}
                          className="px-3 py-1.5 text-xs"
                          onClick={insertCaseStudy}
                        >
                          Insert case-study link
                        </Button>
                      </div>
                    )}
                    <Textarea
                      rows={3}
                      value={reply}
                      placeholder="Write a reply..."
                      onChange={(e) => setReply(e.target.value)}
                    />
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[11px] text-slate-400">
                        Sends from {selected.mailboxEmail}
                      </span>
                      <Button
                        icon={Send}
                        onClick={() => void send()}
                        disabled={sending || !reply.trim()}
                      >
                        {sending ? 'Sending...' : 'Send reply'}
                      </Button>
                    </div>
                  </div>
                )}
                {error && (
                  <div
                    role="alert"
                    className="mt-3 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  >
                    <AlertCircle
                      className="h-4 w-4 shrink-0"
                      strokeWidth={2}
                      aria-hidden
                    />
                    {error}
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
